// Idempotent runtime loader for Authorize.Net Accept.js (inline card fields).
export function loadAcceptJs(src) {
  if (window.Accept?.dispatchData) return Promise.resolve(window.Accept);

  const existing = document.querySelector("script[data-anet-accept-js]");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.Accept));
      existing.addEventListener("error", () => reject(new Error("Could not load Accept.js")));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.anetAcceptJs = "true";
    script.onload = () =>
      window.Accept ? resolve(window.Accept) : reject(new Error("Accept.js loaded but window.Accept is unavailable"));
    script.onerror = () => reject(new Error("Could not load Accept.js"));
    document.head.appendChild(script);
  });
}

function luhn(num) {
  let sum = 0, alt = false;
  for (let i = num.length - 1; i >= 0; i--) {
    let n = parseInt(num[i], 10);
    if (alt) { n *= 2; if (n > 9) n -= 9; }
    sum += n; alt = !alt;
  }
  return sum % 10 === 0;
}

// Returns a friendly error string, or null if the card fields are valid.
export function validateCard(card) {
  const num = (card.cardNumber || "").replace(/\s/g, "");
  if (!/^\d{13,19}$/.test(num) || !luhn(num)) return "Please enter a valid card number.";
  const mm = parseInt(card.month, 10);
  if (!(mm >= 1 && mm <= 12)) return "Expiry month must be between 01 and 12.";
  if (!/^\d{4}$/.test((card.year || "").trim())) return "Enter the 4-digit expiry year (e.g. 2030).";
  const yy = parseInt(card.year, 10);
  const now = new Date();
  if (yy < now.getFullYear() || (yy === now.getFullYear() && mm < now.getMonth() + 1)) {
    return "This card has expired — please check the expiry date.";
  }
  if (!/^\d{3,4}$/.test((card.cardCode || "").trim())) return "CVV must be 3 or 4 digits.";
  if (!(card.zip || "").trim()) return "Enter the billing ZIP code for your card.";
  return null;
}

export function tokenizeCard({ apiLoginID, clientKey }, card) {
  return new Promise((resolve, reject) => {
    if (!window.Accept?.dispatchData) return reject(new Error("Card form not ready yet"));
    const authData = { clientKey, apiLoginID };
    const cardData = {
      cardNumber: (card.cardNumber || "").replace(/\s/g, ""),
      month: (card.month || "").padStart(2, "0"),
      year: card.year || "",
      cardCode: card.cardCode || "",
    };
    if (card.zip) cardData.zip = card.zip.trim();
    if (card.fullName) cardData.fullName = card.fullName.trim();
    window.Accept.dispatchData({ authData, cardData }, (response) => {
      if (response?.messages?.resultCode === "Error") {
        const text = (response.messages.message || []).map((m) => m.text).join("; ");
        return reject(new Error(text || "Card validation failed"));
      }
      const opaque = response?.opaqueData;
      if (!opaque?.dataValue || opaque.dataDescriptor !== "COMMON.ACCEPT.INAPP.PAYMENT") {
        return reject(new Error("Invalid card tokenization response"));
      }
      resolve(opaque);
    });
  });
}
